import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';

const SponsorCard = ({ sponsor, isBanner = false }) => {
  const navigate = useNavigate();

  const getTierBadge = (tier, isAdCard) => {
    if (isAdCard || tier === 'reklam-alani') {
      return { text: 'REKLAM', color: 'bg-gradient-to-r from-gold via-yellow-500 to-gold', textColor: 'text-navy-900' };
    }

    const badges = {
      'ana-sponsor': { text: 'ANA SPONSOR', color: 'bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600', textColor: 'text-white' },
      'premium-sponsor': { text: 'PREMIUM', color: 'bg-gradient-to-r from-yellow-600 via-amber-500 to-yellow-600', textColor: 'text-white' },
      'altin-sponsor': { text: 'ALTIN SPONSOR', color: 'bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500', textColor: 'text-navy-900' },
      'gumus-sponsor': { text: 'GÜMÜŞ SPONSOR', color: 'bg-gradient-to-r from-gray-400 via-slate-300 to-gray-500', textColor: 'text-navy-900' },
      'guvenilir': { text: 'PARTNER', color: 'bg-gradient-to-r from-navy-600 via-navy-500 to-navy-600', textColor: 'text-white' },
      'reklam-alani': { text: 'REKLAM', color: 'bg-gradient-to-r from-gold via-yellow-500 to-gold', textColor: 'text-navy-900' },
    };

    return badges[tier] || { text: 'SPONSOR', color: 'bg-gradient-to-r from-yellow-500 to-amber-500', textColor: 'text-navy-900' };
  };

  const handleCardBlur = (e) => {
    // Kart blur olduğunda durumu sıfırla
    e.currentTarget.style.transform = '';
    e.currentTarget.style.scale = '';
  };

  const handleMouseLeave = (e) => {
    // Mouse karttan ayrıldığında durumu sıfırla
    e.currentTarget.blur();
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };

  const handleCardClick = (e) => {
    if (!isAdCard) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Tıklama sonrası kartın focus'unu kaldır
    const cardElement = e.currentTarget;
    
    if (sponsor.button1_link && sponsor.button1_link !== '' && sponsor.button1_link !== '#') {
      if (sponsor.button1_link.startsWith('http://') || sponsor.button1_link.startsWith('https://')) {
        window.open(sponsor.button1_link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = sponsor.button1_link;
      }
      // Focus'u kaldır ve kartı blur yap
      setTimeout(() => {
        cardElement.blur();
        if (document.activeElement) {
          document.activeElement.blur();
        }
      }, 100);
      return;
    }
    
    if (window.location.pathname === '/contact') {
      setTimeout(() => {
        const contactSection = document.querySelector('[data-contact-section]');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }
      }, 100);
    } else {
      navigate('/contact');
      let attempts = 0;
      const maxAttempts = 10;
      const checkAndScroll = () => {
        attempts++;
        const contactSection = document.querySelector('[data-contact-section]');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (attempts < maxAttempts) {
          setTimeout(checkAndScroll, 200);
        } else {
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }
      };
      setTimeout(checkAndScroll, 300);
    }
  };

  const isAdCard = sponsor.is_ad_card === 1 || sponsor.is_ad_card === true || sponsor.tier === 'reklam-alani';
  const badge = getTierBadge(sponsor.tier, isAdCard);

  // Banner kartı tasarımı
  if (isBanner) {
    return (
      <div 
        className={`group relative w-full rounded-xl overflow-hidden ${isAdCard ? 'cursor-pointer touch-manipulation' : ''} transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:shadow-[0_0_40px_rgba(212,175,55,0.5)]`}
        onClick={isAdCard ? handleCardClick : undefined}
        onBlur={isAdCard ? handleCardBlur : undefined}
        onMouseLeave={isAdCard ? handleMouseLeave : undefined}
        tabIndex={isAdCard ? 0 : undefined}
      >
        <div className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 border-2 border-gold/40 rounded-xl p-4 sm:p-6 md:p-8 min-h-[180px] sm:min-h-[200px] md:min-h-[250px] flex flex-col justify-between">
          {/* Badge */}
          {sponsor.tier !== 'guvenilir' && (
            <div className={`absolute top-2 left-2 sm:top-4 sm:left-4 ${badge.color} ${badge.textColor} px-2 py-1 sm:px-4 sm:py-2 rounded-full text-[10px] sm:text-xs md:text-sm font-bold shadow-lg z-10`}>
              {badge.text}
            </div>
          )}

          {/* Content */}
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-3 sm:gap-4 md:gap-6 mt-6 sm:mt-8 md:mt-0">
            {/* Left side - Image */}
            {sponsor.logo_url && sponsor.logo_url !== '' && (
              <div className="flex-shrink-0 w-full md:w-auto flex justify-center md:justify-start">
                <img
                  src={sponsor.logo_url.startsWith('http') ? sponsor.logo_url : sponsor.logo_url}
                  alt={sponsor.name}
                  className="w-full h-auto max-h-[150px] sm:max-h-[180px] md:max-h-[250px] object-contain filter drop-shadow-2xl"
                  style={{ maxWidth: '100%' }}
                />
              </div>
            )}

            {/* Middle - Info */}
            <div className="flex-1 flex flex-col items-center md:items-start w-full md:w-auto">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gold mb-2 sm:mb-4 text-center md:text-left">{sponsor.name}</h3>

              {/* Description */}
              {sponsor.description && (
                <div className="space-y-1 sm:space-y-2 text-center md:text-left w-full">
                  {sponsor.description.split('\n').map((line, idx) => (
                    <p 
                      key={idx} 
                      className={`${idx === 0 ? 'text-base sm:text-lg md:text-xl font-bold' : 'text-xs sm:text-sm md:text-base'} text-white drop-shadow-md`}
                      style={{ color: idx === 0 ? (sponsor.description_color || '#fbbf24') : '#ffffff' }}
                    >
                      {line}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Right side - Button */}
            {sponsor.button1_text && (
              <div className="flex-shrink-0 w-full md:w-auto">
                <div className="bg-gradient-to-r from-gold via-yellow-500 to-gold text-navy-900 font-bold px-4 py-2 sm:px-6 sm:py-3 rounded-lg shadow-[0_0_20px_rgba(212,175,55,0.6)] text-xs sm:text-sm md:text-base text-center touch-manipulation">
                  {sponsor.button1_text}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Normal grid kartı tasarımı - Size-based classes
  const sponsorSize = sponsor.size || 'medium';
  
  const getCardPadding = () => {
    if (sponsorSize === 'hero') {
      return 'p-4 sm:p-5 md:p-6'; // Hero kart - Daha kompakt padding
    } else if (sponsorSize === 'large') {
      return 'p-5 sm:p-6 md:p-8'; // Büyük kart - Dinamik ve göze çarpan
    } else if (sponsorSize === 'medium') {
      return 'p-4 sm:p-5 md:p-6'; // Orta kart - VIP sponsor
    } else {
      return 'p-3 sm:p-4'; // Küçük kart - Basit
    }
  };

  const getLogoHeight = () => {
    if (sponsorSize === 'hero') {
      return 'h-24 sm:h-28 md:h-32'; // Hero kart - Daha kompakt logo
    } else if (sponsorSize === 'large') {
      return 'h-32 sm:h-40 md:h-48'; // Büyük kart
    } else if (sponsorSize === 'medium') {
      return 'h-24 sm:h-28 md:h-32'; // Orta kart
    } else {
      return 'h-16 sm:h-20 md:h-24'; // Küçük kart
    }
  };

  const getTitleSize = () => {
    if (sponsorSize === 'hero') {
      return 'text-xl sm:text-2xl md:text-3xl'; // Hero kart - Büyük kart gibi
    } else if (sponsorSize === 'large') {
      return 'text-xl sm:text-2xl md:text-3xl'; // Büyük kart
    } else if (sponsorSize === 'medium') {
      return 'text-lg sm:text-xl md:text-2xl'; // Orta kart
    } else {
      return 'text-base sm:text-lg'; // Küçük kart
    }
  };

  const getDescriptionSize = () => {
    if (sponsorSize === 'hero') {
      return 'text-sm sm:text-base md:text-lg'; // Hero kart - Büyük kart gibi
    } else if (sponsorSize === 'large') {
      return 'text-sm sm:text-base md:text-lg'; // Büyük kart
    } else if (sponsorSize === 'medium') {
      return 'text-xs sm:text-sm md:text-base'; // Orta kart
    } else {
      return 'text-xs sm:text-sm'; // Küçük kart - Basit
    }
  };

  const handleSponsorClick = (e) => {
    // Handle logo click - redirect to sponsor link
    if (e.target.tagName === 'IMG' || e.target.closest('.logo-container')) {
      e.stopPropagation();
      if (sponsor.button1_link || sponsor.button1Link) {
        const link = sponsor.button1_link || sponsor.button1Link;
        if (link && link !== '#' && link !== '') {
          if (link.startsWith('http://') || link.startsWith('https://')) {
            window.open(link, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = link;
          }
          return;
        }
      }
      if (sponsor.website_url || sponsor.websiteUrl) {
        const link = sponsor.website_url || sponsor.websiteUrl;
        if (link && link !== '#' && link !== '') {
          if (link.startsWith('http://') || link.startsWith('https://')) {
            window.open(link, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = link;
          }
          return;
        }
      }
    }

    // All cards are clickable
    if (isAdCard) {
      handleCardClick(e);
    } else {
      const link = (sponsor.button1_link || sponsor.button1Link) || 
                   (sponsor.website_url || sponsor.websiteUrl);
      if (link && link !== '#' && link !== '') {
        if (link.startsWith('http://') || link.startsWith('https://')) {
          window.open(link, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = link;
        }
      }
    }
  };

  const isHeroCard = sponsorSize === 'hero';

  // Hero kart için yatay layout (Ana Sponsor alanı)
  if (isHeroCard) {
    return (
      <div
        className="group relative rounded-xl border-2 overflow-hidden w-full cursor-pointer touch-manipulation active:scale-[0.98] flex flex-col md:flex-row bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 border-gold/40 hover:border-gold active:border-gold transition-all duration-500 hover:shadow-[0_0_50px_rgba(212,175,55,0.6)] active:shadow-[0_0_50px_rgba(212,175,55,0.6)] transform hover:-translate-y-2 md:hover:-translate-y-3 hover:scale-[1.02] h-[220px] sm:h-[240px] md:h-[260px]"
        onClick={handleSponsorClick}
        onBlur={handleCardBlur}
        onMouseLeave={handleMouseLeave}
        tabIndex={0}
      >
        {/* Admin panelden yüklenen arkaplan görseli (opsiyonel) - yazıların okunabilmesi için üzerine koyu bir örtü eklenir */}
        {sponsor.bg_image_url && sponsor.bg_image_url !== '' && (
          <>
            <img src={sponsor.bg_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-navy-950/75"></div>
          </>
        )}

        {/* Hero kart için özel animasyonlu gradient overlay - hover'da (masaüstü) veya basılı tutulduğunda (mobil) görünür */}
        <div className="absolute inset-0 bg-gradient-to-r from-gold/0 via-gold/10 to-gold/0 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-500 animate-shimmer"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-gold/5 to-transparent opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-700"></div>
        {/* Animated border glow */}
        <div className="absolute inset-0 rounded-xl border-2 border-gold/0 group-hover:border-gold/60 group-active:border-gold/60 transition-all duration-500 animate-pulse-slow"></div>

        {/* Badge */}
        {sponsor.tier !== 'guvenilir' && (
          <div className={`absolute top-2 right-2 sm:top-3 sm:right-3 ${badge.color} ${badge.textColor} px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[9px] sm:text-[10px] md:text-xs font-bold shadow-lg z-10 flex items-center gap-0.5 sm:gap-1 transition-all duration-300 group-hover:opacity-0 group-active:opacity-0 group-hover:scale-110 group-hover:rotate-12`}>
            {badge.text === 'PREMIUM' && <span className="text-[8px] sm:text-[9px]">✔</span>}
            <span>{badge.text}</span>
          </div>
        )}

        {/* Card Content */}
        <div className={`${getCardPadding()} flex flex-col md:flex-row items-center md:items-center gap-2 md:gap-4 h-full relative z-0 transition-all duration-300 group-hover:blur-[2px] group-active:blur-[2px] flex`}>
          {/* Logo */}
          {sponsor.logo_url && sponsor.logo_url !== '' ? (
            <div className={`md:w-1/4 flex-shrink-0 relative mb-1 ${getLogoHeight()} flex items-center justify-center transition-all duration-300 group-hover:opacity-0 group-active:opacity-0 group-hover:scale-105 group-hover:rotate-2 ${sponsor.tier !== 'guvenilir' ? 'pt-6 sm:pt-8 md:pt-10' : ''}`}>
              <div className="absolute inset-0 bg-gradient-to-r from-gold/20 via-transparent to-gold/20 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-500 rounded-lg blur-xl"></div>
              <img
                src={sponsor.logo_url.startsWith('http') ? sponsor.logo_url : sponsor.logo_url}
                alt={sponsor.name}
                className="max-h-full max-w-full object-contain filter drop-shadow-2xl group-hover:drop-shadow-[0_0_30px_rgba(212,175,55,0.8)] transition-all duration-500 relative z-10"
              />
            </div>
          ) : (
            <h3 className={`${getTitleSize()} font-bold text-gold mb-2 sm:mb-3 md:mb-4 md:text-left text-center transition-opacity duration-300 group-hover:opacity-0 group-active:opacity-0 group-hover:text-gold group-hover:drop-shadow-[0_0_20px_rgba(212,175,55,0.8)]`}>{sponsor.name}</h3>
          )}

          {/* Content wrapper */}
          <div className="md:flex-1 md:flex md:flex-col md:justify-center relative flex flex-col justify-between flex-1">
            {/* Sponsor Name */}
            {sponsor.logo_url && sponsor.logo_url !== '' && (
              <h4 className={`${getTitleSize()} font-bold text-gold mb-1 sm:mb-1.5 md:text-left text-center md:mb-2 drop-shadow-md transition-all duration-300 group-hover:opacity-0 group-active:opacity-0 group-hover:text-gold group-hover:drop-shadow-[0_0_15px_rgba(212,175,55,0.6)] group-hover:scale-105 flex-shrink-0`}>
                {sponsor.name}
              </h4>
            )}

            {/* Hero kart için özel glow efekti */}
            <div className="absolute -inset-1 bg-gradient-to-r from-gold/0 via-gold/20 to-gold/0 opacity-0 group-hover:opacity-100 group-active:opacity-100 blur-xl transition-opacity duration-700 rounded-xl"></div>

            {/* Description */}
            {sponsor.description && (
              <div className="mb-1.5 sm:mb-2 flex-shrink-0 space-y-0.5 sm:space-y-1 transition-opacity duration-300 group-hover:opacity-0 group-active:opacity-0 relative">
                <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gradient-to-b from-gold via-yellow-500 to-gold opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-500 rounded-full"></div>
                {sponsor.description.split('\n').map((line, idx) => (
                  <p
                    key={idx}
                    className={`${getDescriptionSize()} text-white/90 md:text-left text-center relative z-10 group-hover:text-gold/90 ${idx === 0 ? 'font-semibold' : ''} leading-tight sm:leading-normal transition-all duration-300 group-hover:translate-x-1`}
                    style={{ color: idx === 0 ? (sponsor.description_color || '#fbbf24') : 'rgba(255,255,255,0.9)' }}
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}

            {/* Button */}
            {sponsor.button1_text && (
              <div className="md:self-start self-center relative flex-shrink-0 transition-opacity duration-300 group-hover:opacity-0 group-active:opacity-0">
                <div className="absolute inset-0 bg-gradient-to-r from-gold/50 via-yellow-500/50 to-gold/50 opacity-0 group-hover:opacity-100 group-active:opacity-100 blur-md transition-opacity duration-500 rounded-lg"></div>
                <div className="bg-gradient-to-r from-gold via-yellow-500 to-gold text-navy-900 font-bold rounded-lg md:text-left text-center relative z-10 shadow-[0_0_15px_rgba(212,175,55,0.5)] touch-manipulation transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(212,175,55,0.8)] group-hover:scale-105 group-hover:from-yellow-400 group-hover:via-gold group-hover:to-yellow-400 py-2 px-4 sm:px-6 text-xs sm:text-sm md:text-base">
                  {sponsor.button1_text}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hover Logo Overlay - masaüstünde hover'da, mobilde dokunup basılı tutunca görünür */}
        {sponsor.logo_url && sponsor.logo_url !== '' && (
          <div
            className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              const link = (sponsor.button1_link || sponsor.button1Link) ||
                           (sponsor.website_url || sponsor.websiteUrl);

              if (link && link !== '#' && link !== '') {
                if (link.startsWith('http://') || link.startsWith('https://')) {
                  window.open(link, '_blank', 'noopener,noreferrer');
                } else {
                  window.location.href = link;
                }
              }
            }}
          >
            <div className="logo-container transform scale-75 group-hover:scale-125 group-active:scale-125 transition-all duration-500 ease-out" style={{ perspective: '1000px' }}>
              <img
                src={sponsor.logo_url.startsWith('http') ? sponsor.logo_url : sponsor.logo_url}
                alt={sponsor.name}
                className={`${getLogoHeight()} object-contain filter drop-shadow-2xl transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-3`}
                style={{
                  filter: 'drop-shadow(0 20px 40px rgba(212, 175, 55, 0.8)) brightness(1.1)',
                  transformStyle: 'preserve-3d'
                }}
              />
            </div>
          </div>
        )}

        {/* Hover Effect Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-gold/0 via-gold/0 to-gold/0 transition-all duration-300 pointer-events-none rounded-xl z-10 group-hover:from-gold/15 group-hover:via-gold/20 group-hover:to-gold/15 group-hover:animate-pulse-glow group-active:from-gold/15 group-active:via-gold/20 group-active:to-gold/15 group-active:animate-pulse-glow"></div>

        <style jsx>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes pulse-slow {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
          @keyframes pulse-glow {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 0.8; }
          }
          .animate-shimmer {
            animation: shimmer 3s infinite;
          }
          .animate-pulse-slow {
            animation: pulse-slow 3s ease-in-out infinite;
          }
          .animate-pulse-glow {
            animation: pulse-glow 2s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  // Poster kart tasarımı (Büyük / Orta / Küçük) - logo öne çıkan dikey kart + altta güven etiketi
  const tagline = (sponsor.description || '').split('\n')[0].trim() || sponsor.name;
  // Büyük kart 2'li sırada geniş bir alan kapladığı için dikey oranı değil sabit yükseklik kullanır
  const isLargeCard = sponsorSize === 'large';
  const hasBadge = sponsor.tier !== 'guvenilir';
  const hasBgImage = !!(sponsor.bg_image_url && sponsor.bg_image_url !== '');
  // Logo varsayılanda rozetle çakışmaması için rozet varsa daha aşağıda başlar
  const logoTopClass = hasBadge ? 'top-9 sm:top-10 md:top-12' : 'top-3 sm:top-4';

  return (
    <div className="flex flex-col gap-2 sm:gap-2.5 w-full">
      <div
        className="group relative rounded-xl xl:rounded-2xl overflow-hidden border border-white/5 hover:border-gold/50 active:border-gold/50 bg-gradient-card shadow-card hover:shadow-gold-strong active:shadow-gold-strong transition-all duration-500 cursor-pointer touch-manipulation active:scale-[0.98]"
        onClick={handleSponsorClick}
        onBlur={handleCardBlur}
        onMouseLeave={handleMouseLeave}
        tabIndex={0}
      >
        {/* Badge */}
        {hasBadge && (
          <div className={`absolute top-2 right-2 sm:top-3 sm:right-3 ${badge.color} ${badge.textColor} px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[9px] sm:text-[10px] md:text-xs font-bold shadow-lg z-30 flex items-center gap-0.5 sm:gap-1 transition-opacity duration-300 group-hover:opacity-0 group-active:opacity-0`}>
            {badge.text === 'PREMIUM' && <span className="text-[8px] sm:text-[9px]">✔</span>}
            <span>{badge.text}</span>
          </div>
        )}

        {/* Görsel alan */}
        <div className={`relative w-full ${isLargeCard ? 'h-[220px] sm:h-[260px] md:h-[300px]' : 'aspect-[3/4]'}`}>
          {hasBgImage ? (
            /* Admin panelden yüklenen arkaplan görseli - referans sitedeki gibi kartın tamamını kaplar */
            <img
              src={sponsor.bg_image_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            /* Görsel yoksa varsayılan: logonun arkasındaki yumuşak altın parıltı */
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(212,175,55,0.16),transparent_65%)]"></div>
          )}

          {sponsor.logo_url && sponsor.logo_url !== '' ? (
            <>
              {/* Hover'da (masaüstü) veya basılı tutulduğunda (mobil) beliren buzlu cam örtüsü - referans sitedeki reveal efekti */}
              <span className="hidden group-hover:block group-active:block absolute inset-0 bg-white/10 backdrop-blur-lg"></span>

              {/* Logo - varsayılanda üstte küçük, hover'da/dokunuşta tam ortaya kayar (referans sitedeki animasyonun aynısı) */}
              <div className={`absolute z-20 ${getCardPadding()} left-0 right-0 ${logoTopClass} group-hover:top-1/2 group-hover:-translate-y-1/2 group-active:top-1/2 group-active:-translate-y-1/2 transition-all delay-75 duration-300 origin-center flex items-center justify-center ${getLogoHeight()}`}>
                <img
                  src={sponsor.logo_url.startsWith('http') ? sponsor.logo_url : sponsor.logo_url}
                  alt={sponsor.name}
                  className="max-h-full max-w-full object-contain filter drop-shadow-xl"
                />
              </div>
            </>
          ) : (
            <div className={`absolute inset-0 flex items-center justify-center ${getCardPadding()}`}>
              <h3 className={`${getTitleSize()} font-bold text-gold text-center`}>
                {sponsor.name}
              </h3>
            </div>
          )}
        </div>
      </div>

      {/* Güven etiketi - kartın altında, ekremabi879 tarzı */}
      <div className="flex items-center gap-1.5 px-0.5">
        <span className="relative flex items-center justify-center w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-gold/50 opacity-75 animate-ping"></span>
          <FaCheckCircle className="relative w-full h-full text-gold" />
        </span>
        <p className="text-[10px] sm:text-xs text-white/70 truncate">{tagline}</p>
      </div>
    </div>
  );
};

export default SponsorCard;
